
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { TopFrame } from "@/components/dashboard/TopFrame";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import ProjectStructureTab from "@/components/projects/ProjectStructureTab";
import TimeScheduleTab from "@/components/projects/TimeScheduleTab";
import ResourceManagementTab from "@/components/projects/ResourceManagementTab";
import AIProjectAdvisorTab from "@/components/projects/AIProjectAdvisorTab";
import ResourceAssignmentTab from "@/components/projects/ResourceAssignmentTab";
import TaskManagementTab from "@/components/projects/TaskManagementTab";
import CostAnalysisTab from "@/components/projects/CostAnalysisTab";
import { ProjectContext } from "@/components/projects/ProjectStructureTab";
import { Project } from "@/components/projects/types";
import { useUserRole } from "@/components/projects/hooks/useUserRole";
import { useProjectTabs } from "@/hooks/useProjectTabs";

const Projects = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("project-structure");
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [showProjectRequiredAlert, setShowProjectRequiredAlert] = useState(false);

  const { role, loading: roleLoading } = useUserRole(user);
  const { config: projectTabsConfig, isLoading: projectTabsLoading } = useProjectTabs();

  useEffect(() => {
    const checkUser = async () => {
      try {
        const {
          data: {
            session
          }
        } = await supabase.auth.getSession();
        if (!session) {
          navigate('/');
          return;
        }
        setUser(session.user);
        setLoading(false);
      } catch (error) {
        console.error('Error checking session:', error);
        toast.error('Error checking authentication status');
        navigate('/');
      }
    };
    checkUser();
    const {
      data: {
        subscription
      }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session) {
        navigate('/');
      } else {
        setUser(session.user);
      }
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [navigate]);

  // Define visible tabs based on user role and project tab configuration
  const getVisibleTabs = (userRole: string | null, tabsConfig: any) => {
    if (!userRole) return [];
    
    const allTabs = [
      { value: "project-structure", label: "Project Planning", requiresProject: false },
      { value: "time-schedule", label: "Schedule Management", requiresProject: true },
      { value: "resource-management", label: "Resource Management", requiresProject: true },
      { value: "ai-project-advisor", label: "AI Project Advisor (beta)", requiresProject: true },
      { value: "resource-assignment", label: "Resource Assignment", requiresProject: true },
      { value: "task-management", label: "Task Management", requiresProject: true },
      { value: "cost-analysis", label: "Cost Analysis", requiresProject: true }
    ];

    switch (userRole) {
      case "Team member":
      case "Team Lead":
        return allTabs.filter(tab => 
          ["project-structure", "time-schedule", "task-management"].includes(tab.value)
        );
      case "Manager": {
        // Project Managers do NOT see AI Project Advisor or Cost Analysis tabs.
        const managerTabs = ["project-structure", "time-schedule", "resource-management", "resource-assignment", "task-management"];
        return allTabs.filter(tab => managerTabs.includes(tab.value));
      }
      case "Director":
      case "VP":
      case "President":
      case "CEO":
        let directorTabs = ["project-structure", "time-schedule", "resource-management", "resource-assignment", "task-management"];
        
        // Add AI Project Advisor tab only if enabled in configuration
        if (tabsConfig?.ai_projects_advisor) {
          directorTabs.push("ai-project-advisor");
        }
        
        // Add Cost Analysis tab only if enabled in configuration
        if (tabsConfig?.cost_analysis) {
          directorTabs.push("cost-analysis");
        }
        
        return allTabs.filter(tab => directorTabs.includes(tab.value));
      default:
        return [];
    }
  };

  const visibleTabs = getVisibleTabs(role, projectTabsConfig);
  const restrictedTabs = visibleTabs.filter(tab => tab.requiresProject).map(tab => tab.value);

  const handleTabChange = (value: string) => {
    // Check if trying to access a restricted tab without a selected project
    if (restrictedTabs.includes(value) && !selectedProjectId) {
      setShowProjectRequiredAlert(true);
      return; // Don't change the tab
    }
    
    setActiveTab(value);
  };

  if (loading || roleLoading || projectTabsLoading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  if (!user) {
    return null;
  }

  const selectedProject = projects.find(p => p.id === selectedProjectId) || null;
  const gridCols = `grid-cols-${visibleTabs.length}`;

  return (
    <div className="min-h-screen flex flex-col bg-slate-900">
      <TopFrame user={user} hideProgress={true} moduleName="Projects module" />
      <div className="flex-grow overflow-hidden flex flex-col">
        <Card className="border-none bg-secondary flex-grow overflow-hidden flex flex-col m-1">
          <CardContent className="flex-grow overflow-hidden flex flex-col p-1">
            <Tabs value={activeTab} onValueChange={handleTabChange} className="flex-1 container mx-auto px-4 py-6">
              <TabsList className={`grid ${gridCols} w-full justify-center bg-secondary-light/5 border border-primary/20 mb-4 flex-shrink-0`}>
                {visibleTabs.map((tab) => (
                  <TabsTrigger 
                    key={tab.value}
                    value={tab.value} 
                    className={`flex-1 text-primary data-[state=active]:bg-primary data-[state=active]:text-black data-[state=inactive]:bg-secondary ${
                      (tab.requiresProject && !selectedProjectId) ? 'opacity-50 cursor-not-allowed data-[state=inactive]:text-gray-500' : 'data-[state=inactive]:text-white'
                    }`}
                  >
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>
              
              <div className="flex-grow overflow-hidden">
                <ProjectContext.Provider value={{
                  selectedProject,
                  projects,
                  selectedProjectId,
                  loading
                }}>
                  <TabsContent value="project-structure" className="h-full m-0 data-[state=active]:flex data-[state=active]:flex-col overflow-hidden">
                    <ProjectStructureTab 
                      onProjectsChange={setProjects} 
                      onSelectedProjectChange={setSelectedProjectId} 
                      selectedProjectId={selectedProjectId} 
                      user={user}
                    />
                  </TabsContent>
                  
                  {visibleTabs.some(tab => tab.value === "time-schedule") && (
                    <TabsContent value="time-schedule" className="h-full m-0 data-[state=active]:flex data-[state=active]:flex-col overflow-hidden">
                      <TimeScheduleTab user={user} />
                    </TabsContent>
                  )}
                  
                  {visibleTabs.some(tab => tab.value === "resource-management") && (
                    <TabsContent value="resource-management" className="h-full m-0 data-[state=active]:flex data-[state=active]:flex-col overflow-hidden">
                      <ResourceManagementTab />
                    </TabsContent>
                  )}
                  
                  {visibleTabs.some(tab => tab.value === "ai-project-advisor") && (
                    <TabsContent value="ai-project-advisor" className="h-full m-0 data-[state=active]:flex data-[state=active]:flex-col overflow-hidden">
                      <AIProjectAdvisorTab />
                    </TabsContent>
                  )}
                  
                  {visibleTabs.some(tab => tab.value === "resource-assignment") && (
                    <TabsContent value="resource-assignment" className="h-full m-0 data-[state=active]:flex data-[state=active]:flex-col overflow-hidden">
                      <ResourceAssignmentTab user={user} />
                    </TabsContent>
                  )}
                  
                  {visibleTabs.some(tab => tab.value === "task-management") && (
                    <TabsContent value="task-management" className="h-full m-0 data-[state=active]:flex data-[state=active]:flex-col overflow-hidden">
                      <TaskManagementTab user={user} />
                    </TabsContent>
                  )}
                  
                  {visibleTabs.some(tab => tab.value === "cost-analysis") && (
                    <TabsContent value="cost-analysis" className="h-full m-0 data-[state=active]:flex data-[state=active]:flex-col overflow-hidden">
                      <CostAnalysisTab />
                    </TabsContent>
                  )}
                </ProjectContext.Provider>
              </div>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Project Required Alert Dialog */}
      <AlertDialog open={showProjectRequiredAlert} onOpenChange={setShowProjectRequiredAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Project Selection Required</AlertDialogTitle>
            <AlertDialogDescription>
              Please select a project from the Project Planning tab before accessing other tabs.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setShowProjectRequiredAlert(false)}>
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Projects;
